import React, { useRef, useState, useEffect } from 'react';
import Icon from '../Icon';

export default function SignaturePad({ onSignatureChange, initialType = 'typed' }) {
  const canvasRef = useRef(null);
  const [signatureType, setSignatureType] = useState(initialType);
  const [typedSignature, setTypedSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  useEffect(() => {
    if (signatureType === 'typed' && typedSignature) {
      onSignatureChange({
        type: 'typed',
        data: typedSignature,
        timestamp: new Date().toISOString()
      });
    }
  }, [typedSignature, signatureType]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.strokeStyle = 'rgb(15, 23, 42)';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawnSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && hasDrawnSignature) {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      onSignatureChange({
        type: 'drawn',
        data: signatureData,
        timestamp: new Date().toISOString()
      });
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawnSignature(false);
    onSignatureChange(null);
  };

  const switchToType = (type) => {
    setSignatureType(type);
    if (type === 'drawn') {
      setTypedSignature('');
    } else {
      clearCanvas();
    }
    onSignatureChange(null);
  };

  return (
    <div>
      {/* Type selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => switchToType('typed')}
          style={{
            flex: 1,
            padding: '10px 16px',
            fontSize: 13,
            fontWeight: 600,
            color: signatureType === 'typed' ? '#fff' : 'rgb(71, 85, 105)',
            background: signatureType === 'typed' ? 'rgb(14, 116, 144)' : 'transparent',
            border: `1px solid ${signatureType === 'typed' ? 'rgb(14, 116, 144)' : 'rgb(226, 232, 240)'}`,
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            transition: 'background-color 0.15s, border-color 0.15s, color 0.15s'
          }}
        >
          <Icon name="text-outline" style={{ fontSize: 16 }} />
          Type to Sign
        </button>
        <button
          type="button"
          onClick={() => switchToType('drawn')}
          style={{
            flex: 1,
            padding: '10px 16px',
            fontSize: 13,
            fontWeight: 600,
            color: signatureType === 'drawn' ? '#fff' : 'rgb(71, 85, 105)',
            background: signatureType === 'drawn' ? 'rgb(14, 116, 144)' : 'transparent',
            border: `1px solid ${signatureType === 'drawn' ? 'rgb(14, 116, 144)' : 'rgb(226, 232, 240)'}`,
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            transition: 'background-color 0.15s, border-color 0.15s, color 0.15s'
          }}
        >
          <Icon name="brush-outline" style={{ fontSize: 16 }} />
          Draw Signature
        </button>
      </div>

      {/* Signature input area */}
      {signatureType === 'typed' ? (
        <div>
          <label htmlFor="signature-typed-input" style={{ fontSize: 12, fontWeight: 500, color: 'rgb(100, 116, 139)', display: 'block', marginBottom: 6 }}>
            Type your full name
          </label>
          <input
            id="signature-typed-input"
            name="signature"
            type="text"
            data-testid="signature-typed-input"
            value={typedSignature}
            onChange={(e) => setTypedSignature(e.target.value)}
            placeholder="John Doe"
            autoComplete="name"
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: 14,
              border: '2px solid rgb(226, 232, 240)',
              borderRadius: 8,
              outline: 'none',
              transition: 'border-color 0.15s'
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgb(14, 116, 144)'}
            onBlur={(e) => e.target.style.borderColor = 'rgb(226, 232, 240)'}
          />
          {typedSignature && (
            <div
              style={{
                marginTop: 12,
                padding: 20,
                border: '2px solid rgb(226, 232, 240)',
                borderRadius: 8,
                background: '#fff',
                fontFamily: "'Dancing Script', cursive",
                fontSize: 32,
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                color: 'rgb(15, 23, 42)'
              }}
            >
              {typedSignature}
            </div>
          )}
          <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap" rel="stylesheet" />
        </div>
      ) : (
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'rgb(100, 116, 139)', display: 'block', marginBottom: 6 }}>
            Draw your signature below
          </label>
          <canvas
            ref={canvasRef}
            data-testid="enrollment-signature-canvas"
            width={600}
            height={200}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{
              width: '100%',
              height: 200,
              border: '2px solid rgb(226, 232, 240)',
              borderRadius: 8,
              background: '#fff',
              cursor: 'crosshair',
              touchAction: 'none'
            }}
          />
          {hasDrawnSignature && (
            <button
              type="button"
              onClick={clearCanvas}
              style={{
                marginTop: 8,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: 'rgb(71, 85, 105)',
                background: 'transparent',
                border: '1px solid rgb(226, 232, 240)',
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'background-color 0.15s, border-color 0.15s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgb(241, 245, 249)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
